import LPS from '@/domain/anatomy/LPS';
import { Vector3, type Matrix4 } from 'three';
import { multiplyScalar } from '@/domain/geometry/vector3';
import { positionalPart } from '@/domain/geometry/matrix';
import type { CameraState } from '@/lib/threeScene/camera';
import type { CaseInfo, PlannerMode } from '@/views/3d-planner/store/plannerState';
import type { FittedStem } from '@/views/3d-planner/fitting/fittedStem';
import type { FittedCup } from '@/views/3d-planner/fitting/fittedCup';
import { assertDefined } from '@/util/assert';

export type CameraPlacementId = PlannerMode | 'axial' | 'coronal' | 'lateral' | 'bottom';

/**
 * The Model slice this placement reads: scene centre, the fitted components (for the mode-specific
 * camera offsets) and the case (for the operative-side lateral view). Declaring the exact fields
 * rather than the whole `PlannerStore` keeps the dependency honest and lets callers and tests pass a
 * minimal, fully-typed object — the Model store satisfies it structurally.
 */
export interface CameraPlacementModel {
    sceneCentre: Vector3;
    fittedStem: FittedStem | null;
    fittedCup: FittedCup | null;
    case: CaseInfo | null;
}

/**
 * The scene-VM slice this placement reads: the current planner mode, the camera it positions, and
 * the implant group transforms it targets. The scene ViewModel satisfies it structurally.
 */
export interface CameraPlacementScene {
    plannerMode: PlannerMode;
    rendering: { readonly camera: CameraState };
    nodes: {
        stemGroup: { transform: Matrix4 };
        cupGroup: { transform: Matrix4 };
    };
}

/** A camera pose. An absent `offsetDirection` falls back to `DEFAULT_OFFSET_DIRECTION`. */
interface Placement {
    target: Vector3;
    offsetDirection?: Vector3;
}

/** Returns a pose, or `null` to leave the camera alone. */
type PlacementResolver = (
    model: CameraPlacementModel,
    scene: CameraPlacementScene,
) => Placement | null;

/** A total `Record`, so adding an id without a resolver is a compile error. */
const PLACEMENT_RESOLVERS: Record<CameraPlacementId, PlacementResolver> = {
    // No active mode: keep the pose the user orbited to.
    disabled: () => null,

    default: resolveDefaultPlacement,

    stem: (model, scene) => ({
        target: positionalPart(scene.nodes.stemGroup.transform),
        offsetDirection: model.fittedStem?.axesLocal?.paAxis.direction?.clone().negate(),
    }),

    cup: (model, scene) => ({
        target: positionalPart(scene.nodes.cupGroup.transform),
        offsetDirection: model.fittedCup?.apVector.clone().negate(),
    }),

    // Tilted off the pole, not on it: `lookAt` needs a deterministic orientation, and leaving
    // `camera.up` at LPS.Superior keeps orbit clear of gimbal lock.
    axial: (_model, scene) => ({
        target: positionalPart(scene.nodes.stemGroup.transform),
        offsetDirection: nudgeOffPole(LPS.Superior, LPS.Anterior),
    }),

    // As 'axial', off the inferior pole.
    bottom: (_model, scene) => ({
        target: positionalPart(scene.nodes.stemGroup.transform),
        offsetDirection: nudgeOffPole(LPS.Inferior, LPS.Anterior),
    }),

    coronal: (_model, scene) => ({
        target: positionalPart(scene.nodes.stemGroup.transform),
        offsetDirection: LPS.Anterior,
    }),

    lateral: (model, scene) => ({
        target: positionalPart(scene.nodes.stemGroup.transform),
        offsetDirection: LPS.lateral(
            assertDefined(model.case?.operationalSide, 'operational-side'),
        ),
    }),
};

export function setCameraPlacement(
    model: CameraPlacementModel,
    scene: CameraPlacementScene,
    placementId: CameraPlacementId,
) {
    const resolve = PLACEMENT_RESOLVERS[placementId];
    if (!resolve) {
        throw Error(`Cannot apply camera-placement for unknown id '${placementId}'`);
    }

    const placement = resolve(model, scene);
    if (placement === null) {
        return;
    }

    _setPlacement(scene.rendering.camera, placement.target, {
        offsetDirection: placement.offsetDirection,
    });
}

/** `hjcZ === 0` means the stem group is not placed yet, so fall back to the scene centre. */
function resolveDefaultPlacement(
    model: CameraPlacementModel,
    scene: CameraPlacementScene,
): Placement | null {
    if (scene.plannerMode === 'stem') {
        return PLACEMENT_RESOLVERS.stem(model, scene);
    }
    if (scene.plannerMode === 'cup') {
        return PLACEMENT_RESOLVERS.cup(model, scene);
    }

    const hjcZ = positionalPart(scene.nodes.stemGroup.transform).z;
    return {
        target:
            hjcZ !== 0
                ? new Vector3(model.sceneCentre.x, model.sceneCentre.y, hjcZ)
                : model.sceneCentre,
    };
}

//--------------------------------------------------------------------------------------------------

/**
 * Distance from the camera-position to its target for every snap-to-view.
 *
 * One value is used for all directions because the planner is orthographic —
 * camera distance has *no effect on visible scale*, only on the near/far
 * clipping slab. Using a single distance means that no matter which direction
 * the user orbits to from the snap (OrbitControls preserves radius during
 * orbit), the camera stays comfortably past the patient model in every
 * direction.
 *
 * 700 mm is chosen to clear the longest extent of the patient model from the
 * snap target (hip joint): the femur extends ~400–500 mm distal (inferior)
 * from there, so anything less puts the camera *inside* the femur extent and
 * the distal end falls behind the camera and out of the view frustum.
 */
const CAMERA_DISTANCE = 700 as const;

/** The default camera offset-direction is to the anterior, so the camera will look in an AP direction **/
const DEFAULT_OFFSET_DIRECTION = LPS.Anterior;

/** The default for the camera approximate-'up' direction in world-space */
const DEFAULT_UP = LPS.Superior;

/**
 * How far off the superior/inferior pole the axial and bottom views are placed.
 *
 * Set to match the `EPS` Three.js uses internally in `Spherical.makeSafe()`:
 * OrbitControls clamps polar to `[EPS, PI - EPS]` on every update, so manual
 * drag toward a pole reaches exactly this offset and no closer. Snapping to
 * the same offset means the snap-to-view state is bit-for-bit identical to
 * the state manual drag would clamp to, so the two interactions produce the
 * same view. At a 300 mm camera distance, sin(EPS) × 300 ≈ 3 × 10⁻⁴ mm —
 * well below the rendering precision, so the snap view is visually
 * indistinguishable from a true pole view.
 *
 * (Source: three.js/src/math/Spherical.js — `EPS = 0.000001`.)
 */
const POLE_NUDGE_RADIANS = 0.000001;

/**
 * Build a unit vector at `POLE_NUDGE_RADIANS` off `axis`, tilted toward
 * `tiltDirection`. `tiltDirection` must be perpendicular (or nearly so) to
 * `axis`; the returned vector lies in their plane.
 */
function nudgeOffPole(axis: Vector3, tiltDirection: Vector3): Vector3 {
    return axis
        .clone()
        .multiplyScalar(Math.cos(POLE_NUDGE_RADIANS))
        .add(tiltDirection.clone().multiplyScalar(Math.sin(POLE_NUDGE_RADIANS)))
        .normalize();
}

/**
 * The default zoom level — applied by every snap-to-view so the user starts
 * from a consistent baseline regardless of any zooming they did on the
 * previous view. Matches the value `cameraState()` initialises with.
 */
const DEFAULT_ZOOM = 1;

/**
 * Set the position, target, up-direction, and zoom of a camera.
 *
 * Zoom is always reset to `DEFAULT_ZOOM`. Snap-to-view is meant to return the
 * user to a consistent baseline; carrying a previous view's zoom into the new
 * placement makes the snap unpredictable.
 */
function _setPlacement(
    camera: CameraState,
    target: Vector3,
    options?: {
        offsetDirection?: Vector3;
        distance?: number;
        up?: Vector3;
    },
) {
    camera.target.copy(target);
    camera.position
        .copy(target)
        .add(
            multiplyScalar(
                options?.offsetDirection ?? DEFAULT_OFFSET_DIRECTION,
                options?.distance ?? CAMERA_DISTANCE,
            ),
        );
    camera.up.copy(options?.up ?? DEFAULT_UP);
    camera.zoom = DEFAULT_ZOOM;
}
